/**
 * `useChartMarks` — the one way a chart's marks answer a reader (#749, client 7 Oct 2026:
 * "the charts in the analysis side seem rigid/fixed and not interactive").
 *
 * Every mark (a bar, a segment, a matrix cell, a ranked row, a year column on a line chart)
 * gets the same behaviour from `markProps(key, tip, onSelect)`:
 *
 * * **Pointer**: entering or moving over the mark shows the shared `ChartTip` popup at the
 *   pointer; leaving hides it.
 * * **Keyboard**: the mark is a tab stop; focus shows the popup beside the mark (a keyboard
 *   has no cursor, so the mark's own box stands in for one), blur hides it, and Enter or
 *   Space does what a click does where the chart has a selection.
 * * **Touch**: a tap shows the popup; a second tap on the same mark selects it (where the
 *   chart selects). A finger has no hover, so the first tap is the hover.
 * * **Highlight**: the hovered key is state. `rootClass` adds `is-hovering` to the chart's
 *   `svg` and `isHot(key)` names the hovered mark; the stylesheet draws the hovered mark
 *   with an outline and softens its siblings to 70 % opacity.
 *
 * The popup is data, never markup (see `chartTipStore`). Every mark keeps its own native
 * `<title>` as the no-script fallback.
 */
import { useCallback, useRef, useState, type FocusEvent, type KeyboardEvent, type MouseEvent } from 'react';

import { hideChartTip, showChartTip, type ChartTipRow } from './chartTipStore';

/** What a mark's popup says: a context line, a value, and optional lines under it. */
export interface MarkTip {
  label: string;
  value: string;
  rows?: ChartTipRow[];
  /** The rows carry everything; `value` is not drawn (see `ChartTipState.rowsOnly`). */
  rowsOnly?: boolean;
}

/** How long after a touch a click still counts as that touch's tap. */
const TAP_WINDOW_MS = 900;

export interface MarkProps {
  tabIndex: number;
  role?: string;
  'aria-label': string;
  style?: { cursor: string };
  onMouseEnter: (e: MouseEvent<SVGElement>) => void;
  onMouseMove: (e: MouseEvent<SVGElement>) => void;
  onMouseLeave: () => void;
  onTouchStart: () => void;
  onFocus: (e: FocusEvent<SVGElement>) => void;
  onBlur: () => void;
  onKeyDown: (e: KeyboardEvent<SVGElement>) => void;
  onClick: (e: MouseEvent<SVGElement>) => void;
}

export interface ChartMarks<K> {
  hot: K | null;
  /** `' is-hovering'` while a mark is hovered, `''` otherwise: appended to the svg's class. */
  rootClass: string;
  isHot: (key: K) => boolean;
  /** The handlers, tab stop and accessible name for one mark. */
  markProps: (key: K, tip: () => MarkTip, onSelect?: () => void) => MarkProps;
  /** Show a mark's popup at a point (for a chart that tracks the pointer itself). */
  show: (at: { clientX: number; clientY: number }, key: K, tip: MarkTip) => void;
  clear: () => void;
}

/** `label: value`, then each extra line, as one accessible name. */
export function tipText(tip: MarkTip): string {
  const head = tip.value ? `${tip.label}: ${tip.value}` : tip.label;
  const rows = (tip.rows ?? []).map((r) => `${r.label} ${r.value}`.trim());
  return [head, ...rows].filter(Boolean).join('. ');
}

export function useChartMarks<K extends string | number>(): ChartMarks<K> {
  const [hot, setHot] = useState<K | null>(null);
  const armed = useRef<K | null>(null);
  const touchedAt = useRef(0);

  const show = useCallback((at: { clientX: number; clientY: number }, key: K, tip: MarkTip) => {
    setHot(key);
    showChartTip(at, tip.label, tip.value, tip.rows, tip.rowsOnly);
  }, []);

  const clear = useCallback(() => {
    setHot(null);
    hideChartTip();
  }, []);

  const markProps = (key: K, tip: () => MarkTip, onSelect?: () => void): MarkProps => {
    const at = (e: MouseEvent<SVGElement>) => show(e, key, tip());
    return {
      tabIndex: 0,
      role: onSelect ? 'button' : 'listitem',
      'aria-label': tipText(tip()),
      style: onSelect ? { cursor: 'pointer' } : undefined,
      onMouseEnter: at,
      onMouseMove: at,
      onMouseLeave: clear,
      onTouchStart: () => {
        touchedAt.current = Date.now();
      },
      onFocus: (e) => {
        const box = e.currentTarget.getBoundingClientRect();
        show({ clientX: box.right, clientY: box.top + box.height / 2 }, key, tip());
      },
      onBlur: () => {
        armed.current = null;
        clear();
      },
      onKeyDown: (e) => {
        if (e.key !== 'Enter' && e.key !== ' ' && e.key !== 'Spacebar') return;
        e.preventDefault();
        onSelect?.();
      },
      onClick: (e) => {
        const tap = Date.now() - touchedAt.current < TAP_WINDOW_MS;
        if (tap && armed.current !== key) {
          // First tap: the hover a finger cannot do. The second tap on this mark selects.
          armed.current = key;
          show(e, key, tip());
          return;
        }
        armed.current = null;
        onSelect?.();
      },
    };
  };

  return {
    hot,
    rootClass: hot !== null ? ' is-hovering' : '',
    isHot: (key: K) => hot === key,
    markProps,
    show,
    clear,
  };
}

export default useChartMarks;
